import React, { Component } from "react";
import * as equal from "fast-deep-equal";
import { withRouter } from "react-router";
import { withStyles } from "material-ui/styles";
import Button from "material-ui/Button";
import Container from "../controls/Container";
import Form from "../controls/Form";
import CustomTextField from "../controls/textfields/CustomTextField";
import Dropdown from "../controls/dropdown/Dropdown";
import CircularLoader from "../controls/loader/CircularLoader";
import api from "../../api";
import NumberTextField from "../controls/textfields/NumberTextField";
import Message from "../controls/Message";
import Prompt from "../controls/dialog/Prompt";

const styles = theme => ({
  form: {
    marginLeft: 20
  },
  wrapper: {
    position: "relative"
  },
  section: {
    marginTop: 20,
    marginBottom: 20
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 10
  },
  optionRow: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    marginBottom: 10
  },
  optionField: {
    marginRight: 10
  },
  removeButton: {
    marginBottom: 8
  },
  addButton: {
    marginTop: 5,
    marginBottom: 10
  },
  imagePreview: {
    display: "block",
    maxWidth: 180,
    maxHeight: 180,
    marginTop: 10,
    objectFit: "contain"
  }
});

class AddNewProduct extends Component {
  initialData = {
    name: "",
    description: "",
    sku: "",
    barcode: "",
    priceType: "SIMPLE",
    image: "",
    productTypeId: "",
    openingStock: "",
    prices: [
      {
        optionName: "Price",
        price: "",
        quantityRepresented: 1
      }
    ]
  };

  state = {
    data: this.initialData,
    errors: {},
    isLoading: false,
    categories: [],
    showMessage: false,
    isEdit: false,
    showMessageDialog: false
  };

  async componentDidMount() {
    this.setState({ isLoading: true });

    try {
      const categoryResponse = await api.productType.fetchAll();

      const categories = categoryResponse.data.map(category => ({
        value: category.id,
        label: category.description
      }));

      const stateToUpdate = {
        categories,
        isLoading: false
      };

      const { id } = this.props.match.params;

      if (id) {
        const productResponse = await api.posProduct.fetchById(id);
        const product = productResponse.data;

        stateToUpdate.data = {
          ...this.initialData,
          ...product,
          prices:
            product.prices && product.prices.length > 0
              ? product.prices
              : this.initialData.prices
        };

        stateToUpdate.isEdit = true;
      }

      this.setState(stateToUpdate);
    } catch (error) {
      this.showError(error);
    }
  }

  onChange = e => {
    this.setState({
      data: {
        ...this.state.data,
        [e.target.name]: e.target.value
      },
      errors: {
        ...this.state.errors,
        [e.target.name]: ""
      }
    });
  };

  onCategoryChange = value => {
    const productTypeId = value === null ? "" : value;

    this.setState({
      data: {
        ...this.state.data,
        productTypeId
      },
      errors: {
        ...this.state.errors,
        productTypeId: ""
      }
    });
  };

  onPriceTypeChange = value => {
    const priceType = value === null ? "SIMPLE" : value;

    this.setState({
      data: {
        ...this.state.data,
        priceType,
        prices:
          priceType === "SIMPLE"
            ? [
                {
                  optionName: "Price",
                  price:
                    this.state.data.prices &&
                    this.state.data.prices.length > 0
                      ? this.state.data.prices[0].price
                      : "",
                  quantityRepresented: 1
                }
              ]
            : this.state.data.prices &&
              this.state.data.prices.length > 0
            ? this.state.data.prices
            : [
                {
                  optionName: "",
                  price: "",
                  quantityRepresented: 1
                }
              ]
      },
      errors: {
        ...this.state.errors,
        priceType: "",
        prices: ""
      }
    });
  };

  onPriceChange = (index, field, value) => {
    const prices = [...this.state.data.prices];

    prices[index] = {
      ...prices[index],
      [field]: value
    };

    this.setState({
      data: {
        ...this.state.data,
        prices
      },
      errors: {
        ...this.state.errors,
        prices: ""
      }
    });
  };

  addPriceOption = () => {
    this.setState({
      data: {
        ...this.state.data,
        prices: [
          ...this.state.data.prices,
          {
            optionName: "",
            price: "",
            quantityRepresented: 1
          }
        ]
      }
    });
  };

  removePriceOption = index => {
    if (this.state.data.prices.length <= 1) {
      return;
    }

    const prices = this.state.data.prices.filter(
      (_, priceIndex) => priceIndex !== index
    );

    this.setState({
      data: {
        ...this.state.data,
        prices
      }
    });
  };

  onImageChange = e => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.showError(new Error("Please select an image file."));
      return;
    }

    const reader = new FileReader();

    reader.onload = event => {
      this.setState({
        data: {
          ...this.state.data,
          image: event.target.result
        }
      });
    };

    reader.readAsDataURL(file);
  };

  validate = () => {
    const { data } = this.state;
    const errors = {};

    if (!data.name || !String(data.name).trim()) {
      errors.name = "Product name is required";
    }

    if (!data.productTypeId) {
      errors.productTypeId = "Category is required";
    }

    if (!data.priceType) {
      errors.priceType = "Pricing type is required";
    }

    if (!data.prices || data.prices.length === 0) {
      errors.prices = "At least one price is required";
    } else {
      data.prices.forEach((price, index) => {
        if (
          data.priceType === "VARIABLE" &&
          (!price.optionName || !String(price.optionName).trim())
        ) {
          errors[`priceOptionName_${index}`] = "Option name is required";
        }

        if (
          price.price === "" ||
          price.price === null ||
          Number(price.price) <= 0
        ) {
          errors[`price_${index}`] = "Price must be greater than zero";
        }

        if (
          price.quantityRepresented === "" ||
          Number(price.quantityRepresented) <= 0
        ) {
          errors[`quantity_${index}`] =
            "Quantity represented must be greater than zero";
        }
      });
    }

    if (
      !this.state.isEdit &&
      data.openingStock !== "" &&
      Number(data.openingStock) < 0
    ) {
      errors.openingStock = "Opening stock cannot be negative";
    }

    return errors;
  };

  onCancelClick = () => {
    const isDirty = !equal(this.initialData, this.state.data);

    if (isDirty && !this.state.isEdit) {
      this.clearForm();
      return;
    }

    this.props.history.goBack();
  };

  onSubmit = async e => {
    e.preventDefault();

    const errors = this.validate();

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    try {
      const data = {
        ...this.state.data,
        prices: this.state.data.prices.map(price => ({
          optionName:
            this.state.data.priceType === "SIMPLE"
              ? "Price"
              : String(price.optionName).trim(),
          price: Number(price.price),
          quantityRepresented: Number(price.quantityRepresented || 1)
        }))
      };

      if (!this.state.isEdit) {
        data.openingStock = Number(this.state.data.openingStock || 0);
        await this.createNew(data);
      } else {
        delete data.openingStock;
        await this.update(data);
      }
    } catch (error) {
      this.showError(error);
    }
  };

  createNew = async data => {
    const res = await api.posProduct.createNew(data);

    if (res.status === 200) {
      this.showMessage("Product saved successfully");
      this.clearForm();
    } else {
      throw new Error(
        `Unable to create the product. The status code is ${res.status}`
      );
    }
  };

  update = async data => {
    const res = await api.posProduct.update(
      this.props.match.params.id,
      data
    );

    if (res.status === 200) {
      this.clearForm(true);
    } else {
      throw new Error(
        `Unable to update the product. The status code is ${res.status}`
      );
    }
  };

  clearForm = (canShowMessageDialog = false) => {
    this.setState({
      data: {
        ...this.initialData,
        prices: [
          {
            optionName: "Price",
            price: "",
            quantityRepresented: 1
          }
        ]
      },
      showMessageDialog: canShowMessageDialog
    });
  };

  onMessageCloseClick = () => {
    this.setState({
      showMessage: false,
      message: "",
      isError: false
    });
  };

  showMessage = message => {
    this.setState({
      showMessage: true,
      message,
      isError: false
    });
  };

  showError = error => {
    this.setState({
      showMessage: true,
      message: error.message,
      isError: true,
      isLoading: false
    });
  };

  onMessageDialogCloseClick = () => {
    this.setState({ showMessageDialog: false });
    this.props.history.goBack();
  };

  renderPriceFields = () => {
    const { classes } = this.props;
    const { data, errors, isEdit } = this.state;

    return (
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Pricing</div>

        <Dropdown
          name="priceType"
          value={data.priceType}
          error={!!errors.priceType}
          datasource={[
            { value: "SIMPLE", label: "Simple price" },
            { value: "VARIABLE", label: "Variable price" }
          ]}
          onChange={this.onPriceTypeChange}
          placeholder=""
          label="Pricing type"
        />

        {data.priceType === "SIMPLE" && (
          <NumberTextField
            error={!!errors.price_0}
            name="simplePrice"
            value={
              data.prices &&
              data.prices.length > 0 &&
              data.prices[0].price !== undefined
                ? data.prices[0].price
                : ""
            }
            label="Price"
            onChange={e =>
              this.onPriceChange(0, "price", e.target.value)
            }
          />
        )}

        {data.priceType === "VARIABLE" && (
          <div>
            {data.prices.map((price, index) => (
              <div className={classes.optionRow} key={index}>
                <div className={classes.optionField}>
                  <CustomTextField
                    error={!!errors[`priceOptionName_${index}`]}
                    name={`optionName_${index}`}
                    value={price.optionName}
                    label="Option name"
                    onChange={e =>
                      this.onPriceChange(
                        index,
                        "optionName",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className={classes.optionField}>
                  <NumberTextField
                    error={!!errors[`price_${index}`]}
                    name={`price_${index}`}
                    value={price.price}
                    label="Price"
                    onChange={e =>
                      this.onPriceChange(
                        index,
                        "price",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className={classes.optionField}>
                  <NumberTextField
                    error={!!errors[`quantity_${index}`]}
                    name={`quantityRepresented_${index}`}
                    value={price.quantityRepresented}
                    label="Quantity represented"
                    onChange={e =>
                      this.onPriceChange(
                        index,
                        "quantityRepresented",
                        e.target.value
                      )
                    }
                  />
                </div>

                {!isEdit || data.prices.length > 1 ? (
                  <Button
                    className={classes.removeButton}
                    variant="raised"
                    size="small"
                    onClick={() => this.removePriceOption(index)}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}

            <Button
              className={classes.addButton}
              variant="raised"
              color="primary"
              size="small"
              onClick={this.addPriceOption}
            >
              Add price option
            </Button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      data,
      errors,
      isLoading,
      categories,
      showMessage,
      isError,
      message,
      isEdit,
      showMessageDialog
    } = this.state;

    return (
      <Container title={isEdit ? "Edit Product" : "New Product"}>
        <Prompt
          message="The product you entered was saved successfully."
          open={showMessageDialog}
          handleClose={this.onMessageDialogCloseClick}
        />

        <CircularLoader isLoading={isLoading} />

        <Message
          title="Message"
          message={message}
          show={showMessage}
          isError={isError}
          onCloseClick={this.onMessageCloseClick}
          autoClose={!isError}
        />

        <Form
          id="product"
          onSubmit={this.onSubmit}
          onCancel={this.onCancelClick}
          className={classes.form}
        >
          <CustomTextField
            error={!!errors.name}
            name="name"
            value={data.name}
            label="Product name"
            onChange={this.onChange}
          />

          <Dropdown
            name="productType"
            value={data.productTypeId}
            error={!!errors.productTypeId}
            datasource={categories}
            onChange={this.onCategoryChange}
            placeholder=""
            label="Category"
          />

          <CustomTextField
            error={!!errors.description}
            name="description"
            value={data.description}
            label="Description"
            onChange={this.onChange}
          />

          <CustomTextField
            error={!!errors.sku}
            name="sku"
            value={data.sku}
            label="SKU"
            onChange={this.onChange}
          />

          <CustomTextField
            error={!!errors.barcode}
            name="barcode"
            value={data.barcode}
            label="ISBN / GTIN / UPC"
            onChange={this.onChange}
          />

          {this.renderPriceFields()}

          <div className={classes.section}>
            <div className={classes.sectionTitle}>Product picture</div>

            <input
              type="file"
              accept="image/*"
              onChange={this.onImageChange}
            />

            {data.image && (
              <img
                src={data.image}
                alt={data.name || "Product"}
                className={classes.imagePreview}
              />
            )}
          </div>

          {!isEdit && (
            <NumberTextField
              error={!!errors.openingStock}
              name="openingStock"
              value={data.openingStock}
              label="Opening stock"
              onChange={this.onChange}
            />
          )}
        </Form>
      </Container>
    );
  }
}

const component = withStyles(styles, { withTheme: true })(AddNewProduct);

export default withRouter(component);
